import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import MenuItem from '@mui/material/MenuItem';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import Select from '@mui/material/Select';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Typography from '@mui/material/Typography';
import Chart from 'chart.js/auto';

import ThemeColors from '../../../../shared/components/ThemeColors/ThemeColors';
import { useSidebarDarkMode } from '../../../../layouts/private/components/sidebar/sidebarColorMode';
import CampaignMetricCard from './components/CampaignMetricCard';
import { PERIOD_OPTIONS } from './constants/modeGraphicConstants';
import ModeGraphicModal from './ModeGraphicModal';
import useModeGraphicController from './hooks/useModeGraphicController';
import {
  formatMonthLabel,
  hexToRgba,
  normalizeStatus,
} from './utils/modeGraphicFormatters';
import { createModeGraphicTooltip } from './utils/modeGraphicTooltip';

const ModeGraphic = () => {
  const sidebarDark = useSidebarDarkMode();
  const {
    dataGraphic,
    openModal,
    setOpenModal,
    loading,
    loadingTable,
    setLoadingTable,
    chartRef,
    chartInstanceRef,
    lastClickedDateRef,
    modalData,
    setModalData,
    periodFilter,
    setPeriodFilter,
    resetModal,
  } = useModeGraphicController();

  const getStatusTotal = (status) =>
    dataGraphic
      .filter((item) => normalizeStatus(item.final_status) === status)
      .reduce((acc, item) => acc + (parseInt(item.status_count, 10) || 0), 0);

  const totalMessages = dataGraphic
    .reduce((acc, item) => acc + (parseInt(item.status_count, 10) || 0), 0);

  const metricCards = [
    {
      label: 'Total',
      value: totalMessages,
      icon: QueryStatsIcon,
      color: sidebarDark ? ThemeColors.purple700 : ThemeColors.purple600,
      background: sidebarDark ? '#E8DDF7' : '#EFE7FF',
      borderColor: sidebarDark ? '#CBB6EA' : '#D8C5FF',
    },
    {
      label: 'Entregues',
      value: getStatusTotal('delivered'),
      icon: SendIcon,
      color: sidebarDark ? '#136C49' : '#16875A',
      background: sidebarDark ? '#E4F2EB' : '#EEF8F3',
      borderColor: sidebarDark ? '#B9DEC9' : '#CFEADC',
    },
    {
      label: 'Lidas',
      value: getStatusTotal('read'),
      icon: DoneAllIcon,
      color: sidebarDark ? '#126694' : '#177DB3',
      background: sidebarDark ? '#E4F1F8' : '#EEF7FC',
      borderColor: sidebarDark ? '#B9D9EB' : '#CCE7F6',
    },
    {
      label: 'Em espera',
      value: getStatusTotal('waiting'),
      icon: ScheduleIcon,
      color: sidebarDark ? '#7C5405' : '#9A6A06',
      background: sidebarDark ? '#F9EFD8' : '#FFF7E5',
      borderColor: sidebarDark ? '#DEC681' : '#ECD99D',
    },
    {
      label: 'Não confirmadas',
      value: getStatusTotal('not_confirmed'),
      icon: MarkEmailUnreadIcon,
      color: sidebarDark ? '#334155' : '#475569',
      background: sidebarDark ? '#E8EDF3' : '#F2F5F9',
      borderColor: sidebarDark ? '#C0CBD8' : '#D4DDE8',
    },
    {
      label: 'Falhas',
      value: getStatusTotal('failed'),
      icon: ErrorOutlineIcon,
      color: sidebarDark ? '#B91C2B' : '#D92D3A',
      background: sidebarDark ? '#FCE7E9' : '#FFF1F2',
      borderColor: sidebarDark ? '#E4B2B8' : '#EFC7CB',
    },
  ];

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const loadingGradient = ctx.createLinearGradient(0, 0, 0, chartRef.current.height);
    loadingGradient.addColorStop(0, 'rgba(203, 213, 225, 0.2)');
    loadingGradient.addColorStop(1, 'rgba(203, 213, 225, 0.02)');

    const fakeDataset = {
      labels,
      datasets: [
        {
          label: 'Carregando...',
          data: [20, 35, 25, 40, 30, 45],
          borderColor: '#CBD5E1',
          backgroundColor: loadingGradient,
          borderWidth: 2,
          borderDash: [5, 5],
          tension: 0.4,
          fill: true,
          pointRadius: 0,
        },
      ],
    };

    const labelsReal = [...new Set(dataGraphic.map((item) => item.month_year))].sort();
    const totalsByMonth = labelsReal.map((month) =>
      dataGraphic
        .filter((item) => item.month_year === month && item.final_status !== 'total_unknown')
        .reduce((acc, item) => acc + (parseInt(item.status_count, 10) || 0), 0),
    );

    const gradient = ctx.createLinearGradient(0, 0, 0, chartRef.current.height);
    gradient.addColorStop(0, hexToRgba(ThemeColors.purple700, sidebarDark ? 0.38 : 0.32));
    gradient.addColorStop(0.58, hexToRgba(ThemeColors.purple500, sidebarDark ? 0.22 : 0.18));
    gradient.addColorStop(1, hexToRgba(ThemeColors.purple500, sidebarDark ? 0.09 : 0.07));

    const chartData =
      loading || dataGraphic.length === 0
        ? fakeDataset
        : {
            labels: labelsReal,
            datasets: [
              {
                label: 'Total Geral',
                data: totalsByMonth,
                borderColor: ThemeColors.purple700,
                backgroundColor: gradient,
                borderWidth: 2.5,
                tension: 0.42,
                fill: true,
                pointRadius: 4.5,
                pointHoverRadius: 7,
                pointBackgroundColor: '#FFFFFF',
                pointBorderColor: ThemeColors.purple700,
                pointBorderWidth: 2.5,
                pointHoverBackgroundColor: ThemeColors.purple700,
                pointHoverBorderColor: '#FFFFFF',
                pointHoverBorderWidth: 3,
              },
            ],
          };

    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 16, right: 14, bottom: 6, left: 4 },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        onClick: (event) => {
          const activePoints = chartInstanceRef.current.getElementsAtEventForMode(
            event,
            'nearest',
            { intersect: true },
            false,
          );

          if (activePoints.length > 0) {
            const dataIndex = activePoints[0].index;
            const date = chartInstanceRef.current.data.labels[dataIndex];
            const isSameDate = lastClickedDateRef.current === date;

            if (!isSameDate) {
              resetModal();
              setModalData({ label: '', date });
              lastClickedDateRef.current = date;
              setLoadingTable(true);
            }

            setOpenModal(true);
          }
        },
        onHover: (event) => {
          const target = event.native ? event.native.target : event.target;
          const clickablePoints = chartInstanceRef.current.getElementsAtEventForMode(
            event,
            'nearest',
            { intersect: true },
            false,
          );

          target.style.cursor = clickablePoints.length > 0 ? 'pointer' : 'default';
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: createModeGraphicTooltip(dataGraphic),
          },
        },
        scales: {
          x: {
            display: true,
            ticks: {
              color: '#64748B',
              font: { size: 12, weight: '600' },
              maxRotation: 0,
              callback(value) {
                return formatMonthLabel(this.getLabelForValue(value));
              },
            },
            grid: { display: false },
            border: { color: 'rgba(148, 163, 184, 0.22)' },
          },
          y: {
            display: true,
            beginAtZero: true,
            ticks: {
              color: '#8A93A4',
              font: { size: 12, weight: '500' },
              padding: 10,
            },
            grid: { color: sidebarDark ? 'rgba(100, 116, 139, 0.2)' : 'rgba(148, 163, 184, 0.16)', drawBorder: false },
            border: { display: false },
          },
        },
        elements: {
          line: {
            capBezierPoints: true,
          },
        },
      },
    });
  }, [
    chartInstanceRef,
    chartRef,
    dataGraphic,
    lastClickedDateRef,
    loading,
    resetModal,
    setLoadingTable,
    setModalData,
    setOpenModal,
    sidebarDark,
  ]);

  return (
    <>
      <Box
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: '12px', sm: '16px' },
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 1 },
        }}
      >
        <Typography
          sx={{
            color: '#64748B',
            fontSize: { xs: '12px', sm: '13px' },
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          Volume mensal por status das campanhas
        </Typography>

        <Select
          IconComponent={KeyboardArrowDownIcon}
          value={periodFilter}
          onChange={(event) => setPeriodFilter(event.target.value)}
          size="small"
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '10px',
            color: '#475569',
            fontSize: '12px',
            fontWeight: 600,
            height: '34px',
            minWidth: { xs: '100%', sm: '166px' },
            '& .MuiSelect-select': {
              alignItems: 'center',
              display: 'flex',
              py: '7px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E2E8F0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#C8D2E0',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ThemeColors.purple500,
              borderWidth: '1px',
            },
            '& .MuiSelect-icon': {
              color: '#64748B',
              fontSize: '18px',
            },
          }}
        >
          {PERIOD_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '13px' }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: '10px',
          gridAutoColumns: { xs: 'minmax(144px, 1fr)', sm: 'minmax(0, 1fr)' },
          gridAutoFlow: { xs: 'column', sm: 'initial' },
          gridTemplateColumns: { xs: 'none', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
          mt: { xs: '14px', sm: '16px' },
          overflowX: { xs: 'auto', sm: 'visible' },
          px: { xs: 2, sm: 3 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {metricCards.map((item) => (
          <CampaignMetricCard key={item.label} item={item} loading={loading} />
        ))}
      </Box>

      <Box
        sx={{
          height: { xs: '286px', sm: '318px', xl: '336px' },
          position: 'relative',
          margin: { xs: '10px 12px 16px', sm: '12px 24px 24px' },
          p: { xs: 0.5, sm: 1.5 },
          borderRadius: '16px',
          background: '#FFFFFF',
        }}
      >
        <canvas style={{ overflow: 'inherit' }} ref={chartRef} />
      </Box>

      <ModeGraphicModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        data={modalData}
        loading={loadingTable}
        setLoading={setLoadingTable}
      />
    </>
  );
};

export default ModeGraphic;
